import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Get auth token from localStorage (optional for now)
const getAuthToken = () => {
    return localStorage.getItem('access_token');
};

// Axios instance
const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
});

// Add auth token to requests only if available
axiosInstance.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Action Types
export const FETCH_CATEGORIES_REQUEST = 'FETCH_CATEGORIES_REQUEST';
export const FETCH_CATEGORIES_SUCCESS = 'FETCH_CATEGORIES_SUCCESS';
export const FETCH_CATEGORIES_FAILURE = 'FETCH_CATEGORIES_FAILURE';

export const FETCH_MICROCONTROLLERS_REQUEST = 'FETCH_MICROCONTROLLERS_REQUEST';
export const FETCH_MICROCONTROLLERS_SUCCESS = 'FETCH_MICROCONTROLLERS_SUCCESS';
export const FETCH_MICROCONTROLLERS_FAILURE = 'FETCH_MICROCONTROLLERS_FAILURE';

export const FETCH_FIRMWARE_VERSIONS_REQUEST = 'FETCH_FIRMWARE_VERSIONS_REQUEST';
export const FETCH_FIRMWARE_VERSIONS_SUCCESS = 'FETCH_FIRMWARE_VERSIONS_SUCCESS';
export const FETCH_FIRMWARE_VERSIONS_FAILURE = 'FETCH_FIRMWARE_VERSIONS_FAILURE';

export const FETCH_FIRMWARE_CODE_REQUEST = 'FETCH_FIRMWARE_CODE_REQUEST';
export const FETCH_FIRMWARE_CODE_SUCCESS = 'FETCH_FIRMWARE_CODE_SUCCESS';
export const FETCH_FIRMWARE_CODE_FAILURE = 'FETCH_FIRMWARE_CODE_FAILURE';

export const ADD_FIRMWARE_VERSION_REQUEST = 'ADD_FIRMWARE_VERSION_REQUEST';
export const ADD_FIRMWARE_VERSION_SUCCESS = 'ADD_FIRMWARE_VERSION_SUCCESS';
export const ADD_FIRMWARE_VERSION_FAILURE = 'ADD_FIRMWARE_VERSION_FAILURE';

export const UPDATE_FIRMWARE_VERSION_REQUEST = 'UPDATE_FIRMWARE_VERSION_REQUEST';
export const UPDATE_FIRMWARE_VERSION_SUCCESS = 'UPDATE_FIRMWARE_VERSION_SUCCESS';
export const UPDATE_FIRMWARE_VERSION_FAILURE = 'UPDATE_FIRMWARE_VERSION_FAILURE';

export const DELETE_FIRMWARE_VERSION_REQUEST = 'DELETE_FIRMWARE_VERSION_REQUEST';
export const DELETE_FIRMWARE_VERSION_SUCCESS = 'DELETE_FIRMWARE_VERSION_SUCCESS';
export const DELETE_FIRMWARE_VERSION_FAILURE = 'DELETE_FIRMWARE_VERSION_FAILURE';

export const DEPLOY_FIRMWARE_REQUEST = 'DEPLOY_FIRMWARE_REQUEST';
export const DEPLOY_FIRMWARE_SUCCESS = 'DEPLOY_FIRMWARE_SUCCESS';
export const DEPLOY_FIRMWARE_FAILURE = 'DEPLOY_FIRMWARE_FAILURE';

// Action Creators

// Fetch firmware versions for a microcontroller
export const fetchFirmwareVersionsRequest = () => ({
    type: FETCH_FIRMWARE_VERSIONS_REQUEST
});

export const fetchFirmwareVersionsSuccess = (microcontrollerId, versions) => ({
    type: FETCH_FIRMWARE_VERSIONS_SUCCESS,
    payload: { microcontrollerId, versions }
});

export const fetchFirmwareVersionsFailure = (error) => ({
    type: FETCH_FIRMWARE_VERSIONS_FAILURE,
    payload: error
});

// Fetch firmware code for a specific version
export const fetchFirmwareCodeRequest = () => ({
    type: FETCH_FIRMWARE_CODE_REQUEST
});

export const fetchFirmwareCodeSuccess = (code) => ({
    type: FETCH_FIRMWARE_CODE_SUCCESS,
    payload: code
});

export const fetchFirmwareCodeFailure = (error) => ({
    type: FETCH_FIRMWARE_CODE_FAILURE,
    payload: error
});

// Add new firmware version
export const addFirmwareVersionRequest = () => ({
    type: ADD_FIRMWARE_VERSION_REQUEST
});

export const addFirmwareVersionSuccess = (version) => ({
    type: ADD_FIRMWARE_VERSION_SUCCESS,
    payload: version
});

export const addFirmwareVersionFailure = (error) => ({
    type: ADD_FIRMWARE_VERSION_FAILURE,
    payload: error
});

// Update firmware version
export const updateFirmwareVersionRequest = () => ({
    type: UPDATE_FIRMWARE_VERSION_REQUEST
});

export const updateFirmwareVersionSuccess = (version) => ({
    type: UPDATE_FIRMWARE_VERSION_SUCCESS,
    payload: version
});

export const updateFirmwareVersionFailure = (error) => ({
    type: UPDATE_FIRMWARE_VERSION_FAILURE,
    payload: error
});

// Delete firmware version
export const deleteFirmwareVersionRequest = () => ({
    type: DELETE_FIRMWARE_VERSION_REQUEST
});

export const deleteFirmwareVersionSuccess = (versionId) => ({
    type: DELETE_FIRMWARE_VERSION_SUCCESS,
    payload: versionId
});

export const deleteFirmwareVersionFailure = (error) => ({
    type: DELETE_FIRMWARE_VERSION_FAILURE,
    payload: error
});

// Deploy firmware to device
export const deployFirmwareRequest = () => ({
    type: DEPLOY_FIRMWARE_REQUEST
});

export const deployFirmwareSuccess = (result) => ({
    type: DEPLOY_FIRMWARE_SUCCESS,
    payload: result
});

export const deployFirmwareFailure = (error) => ({
    type: DEPLOY_FIRMWARE_FAILURE,
    payload: error
});

// Async Actions (Thunks)

/**
 * Fetch all categories with microcontrollers
 */
export const fetchCategories = () => {
    return async (dispatch) => {
        dispatch({ type: FETCH_CATEGORIES_REQUEST });
        try {
            const response = await axiosInstance.get('/firmware/categories/');
            dispatch({
                type: FETCH_CATEGORIES_SUCCESS,
                payload: response.data
            });
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
            dispatch({
                type: FETCH_CATEGORIES_FAILURE,
                payload: errorMessage
            });
            throw error;
        }
    };
};

/**
 * Fetch microcontrollers grouped by category
 */
export const fetchMicrocontrollersByCategory = () => {
    return async (dispatch) => {
        dispatch({ type: FETCH_MICROCONTROLLERS_REQUEST });
        try {
            const response = await axiosInstance.get('/firmware/microcontrollers/by_category/');
            dispatch({
                type: FETCH_MICROCONTROLLERS_SUCCESS,
                payload: response.data
            });
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
            dispatch({
                type: FETCH_MICROCONTROLLERS_FAILURE,
                payload: errorMessage
            });
            throw error;
        }
    };
};

/**
 * Fetch all firmware versions for a specific microcontroller
 * @param {string} microcontrollerId - The ID or identifier of the microcontroller
 */
export const fetchFirmwareVersions = (microcontrollerId) => {
    return async (dispatch) => {
        dispatch(fetchFirmwareVersionsRequest());
        try {
            const response = await axiosInstance.get('/firmware/versions/', {
                params: { identifier: microcontrollerId }
            });
            dispatch(fetchFirmwareVersionsSuccess(microcontrollerId, response.data));
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
            dispatch(fetchFirmwareVersionsFailure(errorMessage));
            throw error;
        }
    };
};

/**
 * Fetch firmware code/details for a specific version
 * @param {number} versionId - The ID of the firmware version
 */
export const fetchFirmwareCode = (versionId) => {
    return async (dispatch) => {
        dispatch(fetchFirmwareCodeRequest());
        try {
            const response = await axiosInstance.get(`/firmware/versions/${versionId}/`);
            dispatch(fetchFirmwareCodeSuccess(response.data.firmware_code || ''));
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
            dispatch(fetchFirmwareCodeFailure(errorMessage));
            throw error;
        }
    };
};

/**
 * Add a new firmware version
 * @param {Object} firmwareData - The firmware data
 */
export const addFirmwareVersion = (firmwareData) => {
    return async (dispatch) => {
        dispatch(addFirmwareVersionRequest());
        try {
            const formData = new FormData();

            // Add required fields
            formData.append('microcontroller', firmwareData.microcontrollerId);
            formData.append('version_number', firmwareData.versionNumber);
            formData.append('description', firmwareData.description || '');
            formData.append('release_notes', firmwareData.releaseNotes || '');
            formData.append('file_type', firmwareData.fileType);
            formData.append('status', firmwareData.status || 'draft');
            formData.append('is_latest', firmwareData.isLatest || false);

            // Add firmware content based on type
            if (firmwareData.fileType === 'binary' && firmwareData.binaryFile) {
                formData.append('firmware_file', firmwareData.binaryFile);
            } else if (firmwareData.fileType === 'text' && firmwareData.textCode) {
                formData.append('firmware_code', firmwareData.textCode);
            }

            const response = await axiosInstance.post(
                '/firmware/versions/',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            dispatch(addFirmwareVersionSuccess(response.data));
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 
                                 error.response?.data?.detail ||
                                 error.message;
            dispatch(addFirmwareVersionFailure(errorMessage));
            throw error;
        }
    };
};

/**
 * Update an existing firmware version
 * @param {number} versionId - The ID of the firmware version
 * @param {Object} updateData - The data to update
 */
export const updateFirmwareVersion = (versionId, updateData) => {
    return async (dispatch) => {
        dispatch(updateFirmwareVersionRequest());
        try {
            const response = await axiosInstance.patch(
                `/firmware/versions/${versionId}/`,
                updateData
            );

            dispatch(updateFirmwareVersionSuccess(response.data));
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
            dispatch(updateFirmwareVersionFailure(errorMessage));
            throw error;
        }
    };
};

/**
 * Delete a firmware version
 * @param {number} versionId - The ID of the firmware version to delete
 */
export const deleteFirmwareVersion = (versionId) => {
    return async (dispatch) => {
        dispatch(deleteFirmwareVersionRequest());
        try {
            await axiosInstance.delete(`/firmware/versions/${versionId}/`);

            dispatch(deleteFirmwareVersionSuccess(versionId));
            return true;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
            dispatch(deleteFirmwareVersionFailure(errorMessage));
            throw error;
        }
    };
};

/**
 * Deploy firmware to a device
 * @param {number} versionId - The ID of the firmware version
 * @param {string} deviceId - The target device ID
 * @param {string} deviceName - The device name (optional)
 * @param {string} deploymentMethod - The deployment method (default: 'OTA')
 */
export const deployFirmware = (versionId, deviceId, deviceName = '', deploymentMethod = 'OTA') => {
    return async (dispatch) => {
        dispatch(deployFirmwareRequest());
        try {
            const response = await axiosInstance.post(
                `/firmware/versions/${versionId}/deploy/`,
                {
                    device_id: deviceId,
                    device_name: deviceName,
                    deployment_method: deploymentMethod
                }
            );

            dispatch(deployFirmwareSuccess(response.data));
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
            dispatch(deployFirmwareFailure(errorMessage));
            throw error;
        }
    };
};

/**
 * Download firmware file
 * @param {number} versionId - The ID of the firmware version
 */
export const downloadFirmware = async (versionId) => {
    try {
        const token = getAuthToken();
        const response = await axios.get(
            `${API_BASE_URL}/firmware/versions/${versionId}/download/`,
            {
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        );

        // Extract filename from content-disposition header
        const contentDisposition = response.headers['content-disposition'];
        let filename = `firmware_${versionId}.bin`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        // Create a download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return true;
    } catch (error) {
        console.error('Download failed:', error);
        throw error;
    }
};